import type { SuggestionPluginState } from "../suggestions/suggestionRenderer";
import type { MentionAttributes, MentionAttributeSpec, MentionOptions } from "./types";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import {
  mergeAttributes,
  Node,
  VueNodeViewRenderer,
} from "@tiptap/vue-3";
import { preventNodeMarks } from "../preventNodeMarks";
import { createSuggestionRenderer } from "../suggestions";
import MentionItem from "./MentionItem.vue";
import MentionSuggestionList from "./MentionSuggestionList.vue";

export const MentionPluginKey = new PluginKey("mention");
export const MentionSuggestionPluginKey = new PluginKey<SuggestionPluginState>("mention-suggestion");

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mention: {
      /**
       * Starts mention suggestion at current location
       */
      startMention: () => ReturnType;
    };
  }
}

export default Node.create<MentionOptions>({
  name: "mention",
  group: "inline",
  inline: true,
  selectable: true,
  atom: true,

  // increase priority, so that keydown events will be processed in here first before Paragraph node
  priority: 10000,

  addOptions() {
    return {
      suggestion: {
        char: "@",
        pluginKey: MentionSuggestionPluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(" ");

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props as MentionAttributes,
              },
              {
                type: "text",
                text: " ",
              },
            ])
            .run();

          // get reference to `window` object from editor element, to support cross-frame JS usage
          const selection = editor.view.dom.ownerDocument.defaultView?.getSelection();
          if (selection && selection.rangeCount > 0) {
            selection.collapseToEnd();
          }
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          const allow = !!type && !!$from.parent.type.contentMatch.matchType(type);

          return allow;
        },
      },
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute("data-id"),
        renderHTML: attrs => ({
          "data-id": (attrs as MentionAttributes).id,
        }),
      },
      label: {
        default: null,
        parseHTML: element => element.getAttribute("data-label"),
        renderHTML: attrs => ({
          "data-label": (attrs as MentionAttributes).label,
        }),
      },
      mentionerId: {
        default: null,
        parseHTML: element => element.getAttribute("data-mentioner-id"),
        renderHTML: attrs => ({
          "data-mentioner-id": (attrs as MentionAttributes).mentionerId,
        }),
      },
      mentionId: {
        default: null,
        parseHTML: element => element.getAttribute("data-mention-id"),
        renderHTML: attrs => ({
          "data-mention-id": (attrs as MentionAttributes).mentionId,
        }),
      },
    } satisfies MentionAttributeSpec;
  },

  parseHTML() {
    return [{
      tag: `span[data-type="${this.name}"]`,
    }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes({
        "data-type": this.name,
      }, HTMLAttributes),
      `@${node.attrs.label}`,
    ];
  },

  renderText({ node }) {
    const attrs = node.attrs as MentionAttributes;
    return `@${attrs.label || attrs.id}`;
  },

  addNodeView() {
    return VueNodeViewRenderer(MentionItem);
  },

  addCommands() {
    return {
      startMention: () => ({ state, chain }) => {
        let prependSpace = false;
        let appendSpace = false;
        const {
          $from: { nodeBefore },
          $to: { nodeAfter },
        } = state.selection;
        if (nodeBefore && !nodeBefore.textContent.endsWith(" ")) {
          prependSpace = true;
        }
        if (nodeAfter && !nodeAfter.textContent.startsWith(" ")) {
          appendSpace = true;
        }

        const atSymbol = `${prependSpace ? " " : ""}${this.options.suggestion.char}${appendSpace ? " " : ""}`;
        return chain()
          .insertContentAt(state.selection, atSymbol)
          .focus()
          .run();
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
      new Plugin({
        key: MentionPluginKey,
        props: {
          handleDoubleClickOn: (view, position, node) => {
            if (node.type !== this.type) return false;
            const range = {
              from: position,
              to: position + node.nodeSize,
            };
            return this.editor.commands.setTextSelection(range);
          },
        },
      }),

      preventNodeMarks({
        type: this.type,
      }),
    ];
  },
}).configure({
  suggestion: createSuggestionRenderer(MentionSuggestionList),
});
